import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Brain, Shield, Sparkles, Clock, Calendar } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen gradient-bg">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
        {/* Animated background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <img src="/logo.png" alt="SpaceCal logo" data-testid="hero-logo"
            className="w-20 h-20 mx-auto mb-6 rounded-3xl object-cover" style={{ boxShadow: '0 8px 40px rgba(139,92,246,0.45)' }} />
          {/* Empathy Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 glass-card">
            <Heart className="w-4 h-4 text-teal-400" />
            <span className="text-sm font-semibold text-teal-400">Human-Centered AI</span>
          </div>
          
          <h1 
            className="text-5xl sm:text-6xl lg:text-7xl heading-font font-bold tracking-tight mb-6 leading-tight"
            data-testid="hero-title"
          >
            Your Time Should Feel{' '}
            <span className="text-gradient">Humane</span>
            <br />Not Overwhelming
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 body-font leading-relaxed">
            SpaceCal.ai isn't about maximizing output. It's about <span className="text-teal-400 font-semibold">protecting your energy</span>, <span className="text-purple-400 font-semibold">respecting your boundaries</span>, and helping you make calmer, healthier decisions about your day.
          </p>

          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-12 body-font">
            Like a supportive teammate, SpaceCal.ai detects overload, suggests breathing space, and adapts to your work style — only with your consent, never by force.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="teal-btn px-8 py-6 text-lg transition-snap flex items-center gap-2"
              onClick={() => navigate('/auth')}
              data-testid="get-started-btn"
            >
              Start Your Calm Schedule
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Button>
          </div>

          <div className="mt-8 text-sm text-gray-400 body-font">
            7 days free • No credit card • Cancel anytime
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <p className="label-uppercase text-teal-400 mb-4">The Real Problem</p>
          <h2 className="text-4xl sm:text-5xl heading-font font-bold tracking-tight mb-6">
            Traditional Calendars Ignore Your Humanity
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto body-font">
            They pack your day tight, ignore mental fatigue, and treat you like a machine. SpaceCal.ai is different.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="dark-card p-8 transition-snap">
            <div className="text-red-400 text-4xl mb-4">❌</div>
            <h3 className="text-xl heading-font font-bold mb-3 text-gray-300">Back-to-Back Meetings</h3>
            <p className="text-gray-400 body-font">No time to breathe, think, or reset between tasks</p>
          </div>

          <div className="dark-card p-8 transition-snap">
            <div className="text-red-400 text-4xl mb-4">❌</div>
            <h3 className="text-xl heading-font font-bold mb-3 text-gray-300">Mental Overload</h3>
            <p className="text-gray-400 body-font">Your calendar doesn't know when you're drained</p>
          </div>

          <div className="dark-card p-8 transition-snap">
            <div className="text-red-400 text-4xl mb-4">❌</div>
            <h3 className="text-xl heading-font font-bold mb-3 text-gray-300">Forced Optimization</h3>
            <p className="text-gray-400 body-font">Tools that change your schedule without asking</p>
          </div>
        </div>
      </div>

      {/* How SpaceCal.ai Helps */}
      <div className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="label-uppercase text-teal-400 mb-4">How SpaceCal.ai Helps</p>
            <h2 className="text-4xl sm:text-5xl heading-font font-bold tracking-tight">
              An AI That Actually Cares
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="dark-card p-8 transition-snap" data-testid="feature-energy-awareness">
              <div className="w-14 h-14 rounded-xl teal-gradient flex items-center justify-center mb-6">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl heading-font font-bold mb-3">Understands Mental Energy</h3>
              <p className="text-gray-400 body-font leading-relaxed mb-4">
                Track energy levels for tasks. SpaceCal.ai schedules high-energy work when you're fresh, lighter tasks when you're tired.
              </p>
              <p className="text-sm text-teal-400 body-font">
                → Respects your natural rhythms
              </p>
            </div>

            <div className="dark-card p-8 transition-snap" data-testid="feature-overload-detection">
              <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-yellow-400" />
              </div>
              <h3 className="text-2xl heading-font font-bold mb-3">Detects Overload</h3>
              <p className="text-gray-400 body-font leading-relaxed mb-4">
                Warns you when your day is unrealistic. Suggests which tasks to move, reschedule, or skip entirely.
              </p>
              <p className="text-sm text-yellow-400 body-font">
                → Protects you from burnout
              </p>
            </div>

            <div className="dark-card p-8 transition-snap" data-testid="feature-buffer-protection">
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-2xl heading-font font-bold mb-3">Protects Breathing Space</h3>
              <p className="text-gray-400 body-font leading-relaxed mb-4">
                Automatically suggests buffer time between tasks. No back-to-back exhaustion. Just calm transitions.
              </p>
              <p className="text-sm text-purple-400 body-font">
                → Gives you room to think
              </p>
            </div>

            <div className="dark-card p-8 transition-snap" data-testid="feature-consent-based">
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-2xl heading-font font-bold mb-3">Asks, Never Forces</h3>
              <p className="text-gray-400 body-font leading-relaxed mb-4">
                All suggestions require your approval. SpaceCal.ai respects your autonomy and adapts to your choices.
              </p>
              <p className="text-sm text-green-400 body-font">
                → You stay in control
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Style Section */}
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="dark-card p-12 glow-effect">
          <Sparkles className="w-12 h-12 text-teal-400 mx-auto mb-6" />
          <blockquote className="text-2xl sm:text-3xl heading-font font-bold mb-6 text-gray-200">
            "SpaceCal.ai doesn't treat me like a machine. It actually helps me work in a way that feels sustainable."
          </blockquote>
          <p className="text-gray-400 body-font">
            — What our beta users say
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="text-4xl sm:text-5xl heading-font font-bold tracking-tight mb-6">
          Ready for a Calmer Schedule?
        </h2>
        <p className="text-xl text-gray-400 mb-8 body-font">
          Start your 7-day free trial. No pressure, no credit card.
        </p>
        <Button 
          size="lg" 
          className="teal-btn px-12 py-6 text-xl transition-snap"
          onClick={() => navigate('/auth')}
          data-testid="cta-btn"
        >
          Try SpaceCal.ai Free
        </Button>
        <p className="mt-6 text-sm text-gray-500 body-font">
          Join people who've chosen sustainability over burnout
        </p>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo.png" alt="SpaceCal logo" data-testid="footer-logo" className="w-8 h-8 rounded-lg object-cover" />
            <span className="heading-font text-xl font-bold">SpaceCal.ai</span>
          </div>
          <p className="text-sm text-gray-400 body-font mb-2">
            Time management that respects your humanity
          </p>
          <p className="text-xs text-gray-500 body-font">
            © 2026 SpaceCal.ai. Built with empathy, not algorithms.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
